import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAcceptInvitation, usePublicInvitation } from '../api/multiOrg'
import { useAuth } from '../hooks/useAuth'
import { setAuthToken } from '../api/client'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

// Public invitation route — no org segment, no auth guard. It resolves the
// token via GET /api/v1/invitations/{token} and renders one of the several
// states the backend can report (pending/known user, pending/new user,
// revoked, expired, not found). The accept mutation re-uses the app's auth
// helpers so that the user lands inside the org with a valid session.
export const Route = createFileRoute('/invitations/$token')({
    component: InvitationPage,
})

function InvitationPage() {
    const { token } = Route.useParams()
    const { t } = useTranslation('multi-org')
    const navigate = useNavigate()
    const { data: preview, error, isLoading, refetch } = usePublicInvitation(token)
    const accept = useAcceptInvitation()
    const auth = useAuth()

    const [name, setName] = useState('')
    const [password, setPassword] = useState('')
    const [submitError, setSubmitError] = useState<string | null>(null)

    // Refetch the preview on auth state changes — after a login the preview
    // reflects whether the invitation email matches the current user, which
    // toggles the "sign in to accept" CTA to an "Accept" button.
    useEffect(() => {
        refetch()
    }, [auth.isAuthenticated, refetch])

    if (isLoading) {
        return (
            <div
                className="max-w-md mx-auto p-6"
                data-testid="invitation-loading"
            >
                {t('invitation_accept.loading')}
            </div>
        )
    }

    if (error || !preview) {
        return <InvitationError error={error} />
    }

    const status = preview.status
    if (status === 'revoked') {
        return <InvitationMessage kind="revoked" />
    }
    if (status === 'expired') {
        return <InvitationMessage kind="expired" />
    }
    if (status === 'accepted') {
        return <InvitationMessage kind="accepted" />
    }

    // Case: logged in but with a different email than the invitation's.
    const currentEmail = auth.user?.email?.toLowerCase()
    const invitedEmail = preview.email.toLowerCase()
    const wrongUser =
        auth.isAuthenticated && currentEmail && currentEmail !== invitedEmail

    if (wrongUser) {
        return (
            <div
                className="max-w-md mx-auto p-6 space-y-4"
                data-testid="invitation-wrong-user"
            >
                <h1 className="text-xl font-semibold text-foreground">
                    {t('invitation_accept.wrong_user_title')}
                </h1>
                <p className="text-muted-foreground">
                    {t('invitation_accept.wrong_user_description', {
                        invitedEmail: preview.email,
                        currentEmail: auth.user?.email ?? '',
                    })}
                </p>
                <Button
                    data-testid="invitation-wrong-user-logout"
                    onClick={async () => {
                        // Stay on the invitation: once signed out, the page
                        // shows the sign-up form or the "sign in to accept"
                        // link for the invited e-mail.
                        await auth.logout(preview.org_code, { redirect: false })
                    }}
                >
                    {t('invitation_accept.wrong_user_logout')}
                </Button>
            </div>
        )
    }

    const onAccept = async () => {
        setSubmitError(null)
        try {
            const result = await accept.mutateAsync({
                token,
                password: password || undefined,
                name: name || undefined,
            })
            // The backend may return tokens for freshly-created accounts so
            // we can auto-sign-in. If the response shape doesn't include
            // them (logged-in flow), we just navigate.
            const tokens = (result as unknown as {
                access_token?: string
                refresh_token?: string
                expires_in?: number
                mfa_enrollment_required?: boolean
            })
            if (tokens.access_token) {
                setAuthToken(tokens.access_token)
                // A resident or provider lands signed in as their contact
                // (spec 2026-10-06-06), and so does an invitee whose account
                // this accept just created (#1718).
                auth.loginWithTokens({
                    access_token: tokens.access_token,
                    refresh_token: tokens.refresh_token ?? '',
                    expires_in: tokens.expires_in ?? 3600,
                    mfa_enrollment_required: tokens.mfa_enrollment_required,
                    user: result.user,
                    orgCode: result.organization?.code ?? preview.org_code,
                })
                const expiresAt = tokens.expires_in
                    ? new Date(Date.now() + tokens.expires_in * 1000)
                    : new Date(Date.now() + 3600 * 1000)
                localStorage.setItem(
                    'authState',
                    JSON.stringify({
                        isAuthenticated: true,
                        user: result.user,
                        accessToken: tokens.access_token,
                        refreshToken: tokens.refresh_token,
                        expiresAt,
                        orgCode: result.organization?.code,
                        mfaEnrollmentRequired: tokens.mfa_enrollment_required === true,
                    })
                )
            }
            const orgCode = result.organization?.code ?? preview.org_code
            if (orgCode && tokens.mfa_enrollment_required) {
                // The organization requires two-step verification: the new
                // session only lets them enroll a factor first.
                navigate({ to: '/mfa-setup', search: { org: orgCode, next: `/${orgCode}` } })
            } else if (orgCode) {
                navigate({ to: `/${orgCode}` })
            }
        } catch (err) {
            setSubmitError(parseErrorTitle(err))
        }
    }

    const roleLabel = roleLabelFactory(t)

    // Logged-in + matching email → single "Accept" button.
    if (auth.isAuthenticated && !preview.requires_password) {
        return (
            <div
                className="max-w-md mx-auto p-6 space-y-4"
                data-testid="invitation-preview"
            >
                <h1 className="text-xl font-semibold text-foreground">
                    {t('invitation_accept.title', { org: preview.org_name })}
                </h1>
                <p className="text-muted-foreground">
                    {preview.inviter_name
                        ? t('invitation_accept.invited_by', {
                              inviter: preview.inviter_name,
                              role: roleLabel(preview.role_code),
                          })
                        : t('invitation_accept.invited_generic', {
                              role: roleLabel(preview.role_code),
                          })}
                </p>
                <Button
                    data-testid="invitation-accept-btn"
                    disabled={accept.isPending}
                    onClick={onAccept}
                >
                    {accept.isPending
                        ? t('invitation_accept.accepting')
                        : t('invitation_accept.accept_button')}
                </Button>
                {submitError && (
                    <p
                        className="text-sm text-destructive"
                        data-testid="invitation-submit-error"
                    >
                        {submitError}
                    </p>
                )}
            </div>
        )
    }

    // Not logged in + existing user → "Log in to accept" link.
    if (!auth.isAuthenticated && preview.existing_user) {
        const loginHref = `/${preview.org_code}/login?next=${encodeURIComponent(
            `/invitations/${token}`
        )}&email=${encodeURIComponent(preview.email)}`
        return (
            <div
                className="max-w-md mx-auto p-6 space-y-4"
                data-testid="invitation-preview"
            >
                <h1 className="text-xl font-semibold text-foreground">
                    {t('invitation_accept.title', { org: preview.org_name })}
                </h1>
                <p className="text-muted-foreground">
                    {preview.inviter_name
                        ? t('invitation_accept.invited_by', {
                              inviter: preview.inviter_name,
                              role: roleLabel(preview.role_code),
                          })
                        : t('invitation_accept.invited_generic', {
                              role: roleLabel(preview.role_code),
                          })}
                </p>
                <p className="text-sm text-muted-foreground">
                    {t('invitation_accept.sign_in_prompt', { email: preview.email })}
                </p>
                <Button asChild data-testid="invitation-login-btn">
                    <Link to={loginHref}>{t('invitation_accept.login_to_accept')}</Link>
                </Button>
            </div>
        )
    }

    // A resident or provider signs in as their contact (spec 2026-10-06-06):
    // the password is optional, the link they will get by e-mail is enough.
    const passwordOptional = !!preview.kind && preview.kind !== 'staff'

    // Not logged in + new user → signup form (email prefilled, read-only).
    return (
        <div
            className="max-w-md mx-auto p-6 space-y-4"
            data-testid="invitation-preview"
        >
            <h1 className="text-xl font-semibold text-foreground">
                {t('invitation_accept.title', { org: preview.org_name })}
            </h1>
            <p className="text-muted-foreground">
                {preview.inviter_name
                    ? t('invitation_accept.invited_by', {
                          inviter: preview.inviter_name,
                          role: roleLabel(preview.role_code),
                      })
                    : t('invitation_accept.invited_generic', {
                          role: roleLabel(preview.role_code),
                      })}
            </p>
            <p className="text-sm" data-testid="invitation-signup-prompt">
                {passwordOptional
                    ? t('invitation_accept.contact_prompt', { org: preview.org_name, email: preview.email })
                    : t('invitation_accept.signup_prompt', { email: preview.email })}
            </p>
            <div className="space-y-3">
                <div className="space-y-2">
                    <Label htmlFor="invitation-email">Email</Label>
                    <Input id="invitation-email" value={preview.email} readOnly />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="invitation-name">Name</Label>
                    <Input
                        id="invitation-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('invitation_accept.name_placeholder')}
                        data-testid="invitation-name-input"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="invitation-password">Password</Label>
                    <Input
                        id="invitation-password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={passwordOptional
                            ? t('invitation_accept.password_optional_placeholder')
                            : t('invitation_accept.password_placeholder')}
                        data-testid="invitation-password-input"
                    />
                </div>
                <Button
                    data-testid="invitation-signup-submit"
                    disabled={(!password && !passwordOptional) || accept.isPending}
                    onClick={onAccept}
                >
                    {accept.isPending
                        ? t('invitation_accept.accepting')
                        : passwordOptional
                            ? t('invitation_accept.contact_accept_button')
                            : t('invitation_accept.signup_button')}
                </Button>
            </div>
            {submitError && (
                <p
                    className="text-sm text-destructive"
                    data-testid="invitation-submit-error"
                >
                    {submitError}
                </p>
            )}
        </div>
    )
}

function InvitationError({ error }: { error: unknown }) {
    const { t } = useTranslation('multi-org')
    const code = parseErrorCode(error)
    // Map backend error codes to friendly copy. Anything we don't recognize
    // falls back to the generic "cannot be loaded" message.
    const message =
        code === 'invitation_expired'
            ? t('invitation_accept.error_expired')
            : code === 'invitation_revoked'
              ? t('invitation_accept.error_revoked')
              : code === 'invitation_not_found' || code === 'not_found'
                ? t('invitation_accept.error_invalid')
                : code === 'invitation_already_accepted'
                  ? t('invitation_accept.error_accepted')
                  : t('invitation_accept.error_generic')
    return (
        <div
            className="max-w-md mx-auto p-6 space-y-4"
            data-testid="invitation-error"
        >
            <h1 className="text-xl font-semibold text-foreground">
                {t('invitation_accept.error_title')}
            </h1>
            <p className="text-muted-foreground">{message}</p>
        </div>
    )
}

function InvitationMessage({
    kind,
}: {
    kind: 'revoked' | 'expired' | 'accepted'
}) {
    const { t } = useTranslation('multi-org')
    const key =
        kind === 'revoked'
            ? 'invitation_accept.error_revoked'
            : kind === 'expired'
              ? 'invitation_accept.error_expired'
              : 'invitation_accept.error_accepted'
    return (
        <div
            className="max-w-md mx-auto p-6 space-y-4"
            data-testid={`invitation-${kind}`}
        >
            <h1 className="text-xl font-semibold text-foreground">
                {t('invitation_accept.error_title')}
            </h1>
            <p className="text-muted-foreground">{t(key)}</p>
        </div>
    )
}

function roleLabelFactory(t: (k: string) => string) {
    return (code?: string) => {
        if (!code) return '—'
        const key = `roles.${code}`
        const translated = t(key)
        return translated === key ? code : translated
    }
}

function parseErrorTitle(err: unknown): string {
    if (!err) return 'Unknown error.'
    const text = err instanceof Error ? err.message : String(err)
    try {
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed.title === 'string') return parsed.title
    } catch {
        // not JSON — fall through
    }
    return text
}

function parseErrorCode(err: unknown): string | null {
    if (!err) return null
    const text = err instanceof Error ? err.message : String(err)
    try {
        const parsed = JSON.parse(text)
        if (parsed && typeof parsed.code === 'string') return parsed.code
    } catch {
        // not JSON — fall through
    }
    return null
}
